import { Api } from '@monorepo/shared/apiClient';
import { isMultiErrorResponse } from '@monorepo/shared/apiClient/httpClient';
import {
  FormTemplatesCacheEvents,
  invalidateFormTemplatesCache,
} from '@monorepo/shared/cacheRegisters/forms/formTemplatesCacheRegister';
import { useToast } from '@monorepo/shared/contexts';
import { useNavigateKeepingReferrerInfo } from '@monorepo/shared/hooks/navigation/useReferrerUrl';
import { FormTemplateWithUsageResponse } from 'mapistry-shared';
import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';
import { UpdateFormTemplateParams } from '../../../../../shared/apiClient/forms';
import { setCache } from './useFormTemplate';

function useUpdateUrlOnFormTemplateSlugChange() {
  const { formTemplateSlug: formTemplateSlugInUrl } = useParams();
  const navigateKeepingReferrerInfo = useNavigateKeepingReferrerInfo();
  const { pathname } = useLocation();

  const replaceFormSlugInUrl = useCallback(
    (newFormTemplateSlug: string) => {
      if (!formTemplateSlugInUrl) return;

      const newUrl = pathname.replace(
        formTemplateSlugInUrl,
        newFormTemplateSlug,
      );
      navigateKeepingReferrerInfo(newUrl, { replace: true });
    },
    [formTemplateSlugInUrl, pathname, navigateKeepingReferrerInfo],
  );

  const updateUrlOnTemplateSlugChange = useCallback(
    (newFormTemplateSlug: string) => {
      const shouldUpdateUrl = newFormTemplateSlug !== formTemplateSlugInUrl;
      if (shouldUpdateUrl) {
        replaceFormSlugInUrl(newFormTemplateSlug);
      }
    },
    [replaceFormSlugInUrl, formTemplateSlugInUrl],
  );

  return updateUrlOnTemplateSlugChange;
}

export const useFormTemplateUpdate = ({
  resetDirtyState,
}: {
  resetDirtyState: () => void;
}) => {
  const { showUserFriendlyErrorToast, success: showSuccessToast } = useToast();
  const updateUrlOnTemplateSlugChange = useUpdateUrlOnFormTemplateSlugChange();

  const [updateFormTemplate, updateFormTemplateState] = useMutation(
    Api.updateFormTemplate,
    {
      onSuccess: (
        updatedFormTemplate: FormTemplateWithUsageResponse,
        mutationFuncParams: UpdateFormTemplateParams,
      ) => {
        const { slug } = updatedFormTemplate;
        const { organizationId } = mutationFuncParams;

        setCache({ slug, organizationId }, updatedFormTemplate);
        showSuccessToast('Form saved.');
        updateUrlOnTemplateSlugChange(updatedFormTemplate.slug);
        invalidateFormTemplatesCache(FormTemplatesCacheEvents.UPDATE, {});
        resetDirtyState();
      },
      onError: (error) => {
        // In other places, the prepend message purposefully gets ignored when a multi error is caught, so that we don't double up
        // on the same error summary message. Here, we know that we _want to_ show the main error message
        // (this might come from stencil validation, September 2025), thus awkwardly enforcing that.
        const mainErrorMessage = isMultiErrorResponse(error)
          ? error.data.message
          : 'Unable to save form';

        showUserFriendlyErrorToast(error, mainErrorMessage, {
          dontAutoHide: true,
        });
      },
    },
  );

  return [updateFormTemplate, updateFormTemplateState] as const;
};
